import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import CoverImageInput from '@/components/shared/CoverImageInput';
import { Button, TextInput } from '@/components/ui/daisy';
import Fieldset from '@/components/ui/daisy/fieldset';
import { CourseModule } from '@/models/CourseModule';

export type ModuleFormFields = {
  name: string;
  bannerUrl?: string;
};

type ModuleFormProps = {
  module?: CourseModule;
  isLoading?: boolean;
  onSubmit: (data: ModuleFormFields) => void;
};

export default function ModuleForm({
  module,
  isLoading,
  onSubmit,
}: ModuleFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.moduleForm.form',
  });

  const [hasChanges, setHasChanges] = useState(false);

  const defaultValues = useMemo(() => {
    const bannerUrl =
      typeof module?.bannerImg === 'string' ? module.bannerImg : undefined;
    return {
      name: module?.name ?? '',
      bannerUrl,
    };
  }, [module]);

  const {
    watch,
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ModuleFormFields>({
    defaultValues,
  });

  const { field: bannerUrlField } = useController({
    name: 'bannerUrl',
    control,
  });

  useEffect(() => {
    const subscribe = watch(value =>
      setHasChanges(!isEqual(value, defaultValues)),
    );
    return () => {
      setHasChanges(false);
      subscribe.unsubscribe();
    };
  }, [defaultValues, watch]);

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
      <TextInput.Fieldset
        label={t('moduleName')}
        labelAlt={errors?.name?.message}
      >
        <TextInput
          type="text"
          placeholder={t('moduleNamePlaceholder')}
          className="w-full"
          color={!errors?.name?.message ? 'default' : 'error'}
          {...register('name', { required: t('moduleNameRequired') })}
        />
      </TextInput.Fieldset>
      <Fieldset label={t('moduleCover')}>
        <CoverImageInput
          value={bannerUrlField.value}
          onChange={bannerUrlField.onChange}
        />
      </Fieldset>
      <div className="flex justify-end">
        <Button
          type="submit"
          color="primary"
          className="px-16"
          disabled={isLoading || !hasChanges}
          loading={isLoading}
        >
          {t('save')}
        </Button>
      </div>
    </form>
  );
}
